/**
 * Telegram WebApp SDK ustidan yupqa qatlam.
 *
 * DIQQAT: `initDataUnsafe` nomiga mos — undan FAQAT ko'rinish uchun
 * foydalanamiz. Autentifikatsiya har doim xom `initData` satri orqali,
 * uni server HMAC bilan tekshiradi (TZ §13.2).
 *
 * Telegramdan tashqarida (brauzerda `npm run dev`) SDK yo'q. Shunda
 * VITE_DEV_INIT_DATA ishlatiladi — bu faqat ishlab chiqish uchun.
 */

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  section_bg_color?: string;
  section_separator_color?: string;
  destructive_text_color?: string;
}

interface BottomButton {
  text: string;
  isVisible: boolean;
  show(): void;
  hide(): void;
  setText(text: string): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  enable(): void;
  disable(): void;
}

interface BackButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface HapticFeedback {
  impactOccurred(style: "light" | "medium" | "heavy"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name?: string; language_code?: string };
    start_param?: string;
  };
  version: string;
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  isExpanded: boolean;
  viewportStableHeight: number;
  MainButton: BottomButton;
  BackButton: BackButton;
  HapticFeedback: HapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  showAlert(message: string, cb?: () => void): void;
  setHeaderColor(color: string): void;
  setBackgroundColor?(color: string): void;
  disableVerticalSwipes?(): void;
  isVersionAtLeast?(version: string): boolean;
}

/**
 * Brend foni (styles.css dagi --navy-deep bilan bir xil).
 * Ilova o'z quyuq foniga ega, shuning uchun Telegram chrome'i ham
 * shunga bo'yaladi — aks holda sarlavha oq bo'lib, ilova ustida
 * yorug' chiziq qolib ketadi.
 */
const BRAND_BG = "#091a33";

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const webApp: TelegramWebApp | undefined =
  typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;

export const insideTelegram = Boolean(webApp?.initData);

/**
 * Mijoz shu Bot API versiyasini qo'llab-quvvatlaydimi.
 *
 * NEGA KERAK: qo'llab-quvvatlanmagan usulni chaqirsak SDK xato
 * TASHLAMAYDI — jimgina konsolga ogohlantirish yozadi va hech narsa
 * qilmaydi. Ya'ni try/catch yordam bermaydi, oldindan tekshirish kerak.
 *
 * `isVersionAtLeast` ning o'zi ham 6.1 da paydo bo'lgan; yo'q bo'lsa
 * mijoz eski deb hisoblanadi.
 */
export function supports(version: string): boolean {
  return Boolean(webApp?.isVersionAtLeast?.(version));
}

/**
 * Server uchun autentifikatsiya satri.
 *
 * `import.meta.env.DEV` SHART: Vite `import.meta.env.VITE_*` havolalarini
 * build vaqtida SATRGA almashtiradi — runtime tekshiruvi emas. Guard
 * bo'lmasa, ishlab chiqarish build'ida ham haqiqiy imzolangan initData
 * (masalan admin hisobiga) JS bundle ichida ochiq matn sifatida qoladi
 * va uni bundle'ni yuklab olgan HAR KIM o'qiy oladi. `DEV` production'da
 * statik `false` bo'lgani uchun minifikator butun blokni butunlay olib
 * tashlaydi — qiymat bundle'ga umuman tushmaydi.
 */
export function getInitData(): string {
  if (webApp?.initData) return webApp.initData;
  if (import.meta.env.DEV) {
    const dev = import.meta.env.VITE_DEV_INIT_DATA;
    return typeof dev === "string" ? dev : "";
  }
  return "";
}

/** Telegram mavzusini CSS o'zgaruvchilariga ko'chiradi. */
function applyTheme(): void {
  if (!webApp) return;
  const root = document.documentElement;
  const params = webApp.themeParams;
  const map: Record<string, string | undefined> = {
    "--tg-bg": params.bg_color,
    "--tg-text": params.text_color,
    "--tg-hint": params.hint_color,
    "--tg-link": params.link_color,
    "--tg-button": params.button_color,
    "--tg-button-text": params.button_text_color,
    "--tg-secondary-bg": params.secondary_bg_color,
    "--tg-section-bg": params.section_bg_color,
    "--tg-separator": params.section_separator_color,
    "--tg-destructive": params.destructive_text_color,
  };
  for (const [name, value] of Object.entries(map)) {
    if (value) root.style.setProperty(name, value);
  }
  root.dataset.scheme = webApp.colorScheme;
}

export function initTelegram(): void {
  if (!webApp) return;
  webApp.ready();
  webApp.expand();
  applyTheme();

  // Telegram sarlavhasi va foni ilovaning navy foniga qo'shilib ketsin.
  // 6.1 dan oldin bu usullar yo'q — eski mijoz Telegram mavzusida
  // qoladi, ilova baribir ishlaydi.
  if (supports("6.1")) {
    webApp.setHeaderColor(BRAND_BG);
    webApp.setBackgroundColor?.(BRAND_BG);
  }

  // Vertikal svayp Mini App'ni tasodifan yopib yubormasin (7.7+)
  if (supports("7.7")) webApp.disableVerticalSwipes?.();
}

/** Tebranish javobi. 6.1 dan oldin yo'q — jimgina o'tkazib yuboriladi. */
export function haptic(kind: "light" | "medium" | "success" | "error"): void {
  if (!supports("6.1")) return;
  const h = webApp?.HapticFeedback;
  if (!h) return;
  if (kind === "success" || kind === "error") h.notificationOccurred(kind);
  else h.impactOccurred(kind);
}

/** Tashqi havolani Telegram brauzerida ochadi. */
export function openExternal(url: string): void {
  if (webApp) webApp.openLink(url);
  else window.open(url, "_blank", "noopener,noreferrer");
}

/** Telegram ichidagi havola (ulashish oynasi). */
export function openTelegram(url: string): void {
  if (webApp) webApp.openTelegramLink(url);
  else window.open(url, "_blank", "noopener,noreferrer");
}

/** Xabar oynasi. `showAlert` 6.2 da qo'shilgan — eskisida brauzerniki. */
export function alertUser(message: string): void {
  if (webApp && supports("6.2")) webApp.showAlert(message);
  else window.alert(message);
}
